import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import ClassIcon from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_classes.svg';
import { Footer, Button, DataTable, Search } from '@3plearning/react-components';
import overviewActions, { CLASS_OVERVIEW_DATA_RETRIEVED } from './overview-actions';
import selectClassActions from './selected-class-actions';
import css from './overview.scss';
import modalActions from '../../components/modal/modal-actions';
import academicYearActions, { ACADEMIC_YEAR_RETRIEVED } from './academic-year-actions';
import datePickerActions from '../../components/date-picker/date-picker-actions';
import { TABLE_CONFIG, NO_CLASSES_FOUND_LOAD, NO_CLASSES_FOUND_SEARCH } from './overview-constants';
import IconMessage from '../../components/icon-message/icon-message';
import HeaderWithAcademicYear from '../../components/header-with-academic-year/header-with-academic-year';
import localeActions from '../../locale/locale-actions';
import { DatePicker } from '../../components/date-picker/date-picker';
import { THIS_SCHOOL_YEAR } from '../../components/date-picker/components/date-picker-trans/date-picker-constants';
import { translateTableLabels } from '../../locale/locale-utils';
import CustomButton from '../../components/button';
import {
    calculateTotal,
    exportStudentCertificates,
    CERTIFICATE_DOWNLOAD_LIMIT
} from '../../helpers/utils';
import DownloadIcon from '../../assets/images/download-icon.svg';
import { ALL_NEW, FULL_COLOR, PRINT_FRIENDLY } from '../certificates/download-constants';
import PopUpMenu from './sub-components/popup-menu';
import { ToastContainer } from '../../components/toast/toast-container';
import downloadActions from '../certificates/download-certificates-actions';

/**
 * Overview Redux component
 * Receives data via 'props'
 * Base component for Overview page, displaying data table, search bar, date picker, etc.
 */
export class Overview extends Component {
    static contextTypes = {
        router: PropTypes.object
    };

    static propTypes = {
        classOverviewData: PropTypes.array,
        selectedDateRange: PropTypes.shape({
            type: PropTypes.string,
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        getClassOverviewData: PropTypes.func,
        openModal: PropTypes.func,
        localeData: PropTypes.object,
        setTranslationData: PropTypes.func,
        getAcademicYear: PropTypes.func,
        updateSelectedDateRange: PropTypes.func,
        setSelectedClass: PropTypes.func,
        productId: PropTypes.string,
        selectedClassroom: PropTypes.shape({
            id: PropTypes.number,
            class: PropTypes.string
        }),
        academicYear: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        downloadAllSchoolOrClassCerts: PropTypes.func,
        downloadCertificatesState: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            tableConfig: null,
            classOverviewData: [],
            selectedClassroom: null,
            searchFieldText: '',
            localeData: {},
            shouldDisplayDropdown: false,
            showToast: false,
            dropdownTriggered: false
        };
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    componentDidMount() {
        document.addEventListener('click', this.handleOutsideClick);
        const { updateSelectedDateRange, selectedDateRange, setTranslationData } = this.props;

        if (!selectedDateRange.startDate) {
            this.getAcademicYear();
        } else {
            updateSelectedDateRange(selectedDateRange.type, selectedDateRange.startDate, selectedDateRange.endDate);
        }

        setTranslationData();
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    }

    componentWillReceiveProps(nextProps) {
        const { selectedDateRange } = this.props;

        if (selectedDateRange !== nextProps.selectedDateRange) {
            const { startDate, endDate } = nextProps.selectedDateRange;
            this.getClassOverviewData(startDate, endDate);
        }
    }

    getAcademicYear() {
        const { getAcademicYear, updateSelectedDateRange } = this.props;

        getAcademicYear().then((action) => {
            if (action.type === ACADEMIC_YEAR_RETRIEVED) {
                const { startDate, endDate } = action.payload;
                updateSelectedDateRange(THIS_SCHOOL_YEAR, startDate, endDate);
            } else {
                this.displayError(action.error);
            }
        }, error => this.displayError(error.message));
    }

    getClassOverviewData(startDate, endDate) {
        const { getClassOverviewData, productId } = this.props;

        getClassOverviewData(startDate, endDate, productId).then((action) => {
            if (action.type === CLASS_OVERVIEW_DATA_RETRIEVED) {
                this.setClassOverviewData(action.payload.data);
            } else {
                this.displayError(action.error);
            }
        }, error => this.displayError(error.message));
    }

    setClassOverviewData(classOverviewData, filterText = this.state.filterText) {
        const { selectedClassroom, localeData } = this.props;

        this.setState({
            ...this.state,
            searchFieldText: filterText
        });
        let emptyDataText = localeData[NO_CLASSES_FOUND_LOAD];
        if (filterText) {
            classOverviewData = this.filterClasses(classOverviewData, filterText);
            emptyDataText = localeData[NO_CLASSES_FOUND_SEARCH];
        }
        const fullTotal = calculateTotal(classOverviewData).toString();

        this.setConfig(fullTotal, emptyDataText);
        this.setTableData(classOverviewData, selectedClassroom);
    }

    filterClasses = (data, filterText) => {
        filterText = filterText.toLowerCase();
        return data.filter(c => c.class.toLowerCase().indexOf(filterText) > -1);
    };

    highlightClass = (classrooms, selectedClassroom) => {
        if (selectedClassroom) {
            classrooms = classrooms.map(item => (
                {
                    ...item,
                    className: selectedClassroom.id === item.id ? css.selected : null
                }
            ));
        }
        return classrooms;
    };

    setTableData = (data, selectedClassroom) => {
        data = this.highlightClass(data, selectedClassroom);
        this.setState({
            classOverviewData: data,
            selectedClassroom
        });
    };

    setConfig = (fullTotal, emptyDataText) => {
        const tableConfig = cloneDeep(TABLE_CONFIG);
        const { localeData } = this.props;
        tableConfig.onSelectRow = this.onSelectRow;
        translateTableLabels(tableConfig, localeData, false, fullTotal);
        if (emptyDataText) {
            tableConfig.emptyDataMessage = <IconMessage iconSrc={ClassIcon} message={emptyDataText} />;
        }
        this.setState({ tableConfig });
    };

    displayError = (error) => {
        const { localeData } = this.props;
        this.props.openModal({
            title: localeData['Modal - Oops'],
            content: <p>{error}</p>,
            buttons: [{ title: localeData['Modal - TryAgain'], primary: true }]
        });
    };

    onNextClick = () => {
        const { setSelectedClass } = this.props;
        const { selectedClassroom } = this.state;
        setSelectedClass(selectedClassroom);
        const { history } = this.context.router;
        history.push('/class-details');
    };

    onSelectRow = (row) => {
        const { classOverviewData } = this.state;
        this.setTableData(classOverviewData, row);
        this.setState({ selectedClassroom: row });
    };

    showPrintModal = ({
        total,
        selectedDateRange,
        localeData,
        classroomId,
        downloadAllSchoolOrClassCerts,
        filterText
    }) => {
        this.props.openModal({
            title: `${total} ${total === 1 ? localeData['Label - Certificate'] : localeData['Label - Certificates']}`,
            content: `${localeData['PrintModal - SelectVersion']}`,
            certificatesCount: total,
            buttons: [
                {
                    title: `${localeData['Label - FullColour']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: FULL_COLOR,
                    handler: () => downloadAllSchoolOrClassCerts({
                        fullColor: true,
                        classroomId,
                        filterText,
                        startDate: selectedDateRange.startDate,
                        endDate: selectedDateRange.endDate,
                        downloadType: FULL_COLOR
                    })

                },
                {
                    title: `${localeData['Label - PrintFriendly']}`,
                    primary: true,
                    icon: DownloadIcon,
                    btnType: PRINT_FRIENDLY,
                    handler: () => downloadAllSchoolOrClassCerts({
                        fullColor: false,
                        classroomId,
                        filterText,
                        startDate: selectedDateRange.startDate,
                        endDate: selectedDateRange.endDate,
                        downloadType: PRINT_FRIENDLY
                    })
                }],
            useNewModal: true
        });
    }


    downloadCertificateOrReports = (event) => {
        const { classOverviewData, searchFieldText } = this.state;
        const {
            selectedDateRange,
            localeData,
            downloadAllSchoolOrClassCerts
        } = this.props;
        const totalCertificates = calculateTotal(classOverviewData);

        this.setState({
            ...this.state,
            shouldDisplayDropdown: false
        });

        if (event.target.id === '0') {
            if (totalCertificates > CERTIFICATE_DOWNLOAD_LIMIT) {
                this.setState({
                    ...this.state,
                    shouldDisplayDropdown: false,
                    showToast: true
                });
            } else {
                this.showPrintModal({
                    total: totalCertificates,
                    selectedDateRange,
                    localeData,
                    classroomId: 0,
                    filterText: searchFieldText,
                    downloadAllSchoolOrClassCerts
                });
            }
        }

        const showToastTimer = setTimeout(() => {
            this.setState({
                ...this.state,
                showToast: false
            });
            clearTimeout(showToastTimer);
        }, 3000);
    }

    handleOutsideClick = (event) => {
        if (this.state.shouldDisplayDropdown && !this.state.dropdownTriggered) {
            this.setState({ shouldDisplayDropdown: false });
            event.stopPropagation();
        }
        this.setState({
            dropdownTriggered: false
        });
    };

    render() {
        const {
            tableConfig,
            classOverviewData,
            selectedClassroom,
            shouldDisplayDropdown,
            showToast
        } = this.state;
        const {
            academicYear,
            selectedDateRange,
            updateSelectedDateRange,
            localeData,
            downloadCertificatesState
        } = this.props;
        const isDownloadActive = downloadCertificatesState.isActive;
        const disableButton = !selectedClassroom;
        const title = localeData['Header - OverviewTitle'];
        const classNameLabel = localeData['Label - ClassName'];
        const nextLabel = localeData['Label - Next'];
        const totalCertificatesInAllClasses = calculateTotal(classOverviewData);
        const downloadDisabled = totalCertificatesInAllClasses === 0;
        const options = [
            { label: 'certificate', value: localeData['DropDown - All Certificates'] }
        ];
        const toastData = {
            messageData: {
                message: localeData['Toast - Too Many Certificate Error Message'],
                type: 'fail',
                hasClose: true
            }
        };

        return (
            <div className={css.component}>
                <HeaderWithAcademicYear title={title} academicYear={academicYear} />
                <div className={css['main-content']}>
                    <div className={css['header-container']}>
                        <div className={css['search-container']}>
                            <Search
                                onType={(filterText) => {
                                    this.setClassOverviewData(this.props.classOverviewData, filterText);
                                }}
                                placeholder={classNameLabel}
                            />
                        </div>

                        {showToast && (
                            <ToastContainer toastData={toastData} />
                        )}


                        <div className={css['download-search-container']}>
                            <CustomButton
                                className={css['export-button']}
                                text={localeData['Label - Export']}
                                primary={false}
                                type={CustomButton.LOZENGE}
                                size={CustomButton.LARGE}
                                onClick={() => {
                                // eslint-disable-next-line max-len
                                    exportStudentCertificates(0, this.state.searchFieldText, selectedDateRange);
                                }}
                            />

                            <CustomButton
                                className={css['download-button']}
                                text={
                                    isDownloadActive ?
                                        localeData['Label - Preparing download'] : localeData['Label - Download']
                                }
                                primary={false}
                                type={CustomButton.LOZENGE}
                                size={CustomButton.SMALL}
                                icon={isDownloadActive ? null : DownloadIcon}
                                alignIcon={CustomButton.RIGHT}
                                btnType={ALL_NEW}
                                disabled={isDownloadActive}
                                onClick={() => {
                                    this.setState({
                                        ...this.state,
                                        shouldDisplayDropdown: !this.state.shouldDisplayDropdown,
                                        dropdownTriggered: !this.state.shouldDisplayDropdown
                                    });
                                }}
                            />

                            {shouldDisplayDropdown && (
                                <PopUpMenu
                                    downloadDisabled={downloadDisabled}
                                    isClassLevel={false}
                                    options={options}
                                    downloadCertificateOrReports={this.downloadCertificateOrReports}
                                />
                            )}


                            <DatePicker
                                currentDate={selectedDateRange}
                                academicYear={academicYear}
                                onDateChange={updateSelectedDateRange}
                                localeData={localeData}
                            />
                        </div>
                    </div>
                    <div className={css['table-container']}>
                        <DataTable
                            className={css['data-table']}
                            config={tableConfig}
                            data={classOverviewData}
                            onSelect={this.onSelectRow}
                        />
                    </div>
                </div>
                <Footer alignChildren={Footer.RIGHT}>
                    <Button
                        text={nextLabel}
                        type={Button.LOZENGE}
                        size={Button.LARGE}
                        primary
                        onClick={this.onNextClick}
                        disabled={disableButton}
                    />
                </Footer>
            </div>
        );
    }
}

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from CombinedReducers.
 */
function mapStateToProps(state) {
    return {
        localeData: state.localeData,
        classOverviewData: state.classOverviewData,
        selectedDateRange: state.selectedDateRange,
        productId: state.productId,
        selectedClassroom: state.selectedClass,
        academicYear: state.academicYear,
        studentCertificates: state.studentCertificates,
        downloadCertificatesState: state.downloadCertificatesState
    };
}

/**
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        setTranslationData: localeActions.setTranslationData,
        getClassOverviewData: overviewActions.getClassOverviewData,
        openModal: modalActions.openModal,
        getAcademicYear: academicYearActions.getAcademicYear,
        updateSelectedDateRange: datePickerActions.updateSelectedDateRange,
        setSelectedClass: selectClassActions.setSelectedClass,
        downloadAllSchoolOrClassCerts: downloadActions.downloadAllSchoolOrClassCerts
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Overview);



// WEBPACK FOOTER //
// ./containers/overview/overview.js